import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaArrowLeft } from "react-icons/fa";
import { getSalesmanRedemptionStats } from "../../redux/actions/orderActions";

// Card summary config — used when the page is opened via /summary/:cardType
const CARD_CONFIG = {
  salesmen: { title: "All Salesmen", type: "salesmen" },
  accumulated: {
    title: "Accumulated QR Codes",
    type: "qr",
    statusFilter: "Accumulated",
  },
  redeemed: {
    title: "Redeemed QR Codes",
    type: "qr",
    statusFilter: "Redeemed",
  },
  scanned: { title: "All Scanned QR Codes", type: "qr", statusFilter: null },
};

const SalesmanRedemptionDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Only one of these will be present depending on which route matched:
  //   /dashboard/redeemedstatistcis/:salesmanId
  //   /dashboard/redeemedstatistcis/summary/:cardType
  const { salesmanId, cardType } = useParams();
  const mode = cardType ? "summary" : "salesman";
  const config = mode === "summary" ? CARD_CONFIG[cardType] : null;

  const { userInfo } = useSelector((state) => state.auth);

  const initialRange = location.state?.dateRange;
  const [dateRange, setDateRange] = useState([
    initialRange?.[0]
      ? new Date(initialRange[0])
      : dayjs().startOf("month").toDate(),
    initialRange?.[1] ? new Date(initialRange[1]) : new Date(),
  ]);
  const [startDate, endDate] = dateRange;

  const {
    loading = false,
    data: salesmanData = [],
    error = null,
  } = useSelector((state) => state.redemptionStats || {});

  // Single fetch (unfiltered by salesman) covers every mode: the response
  // already includes each salesman's own qr_codes, so we can derive
  // whichever view we need from it without extra API calls.
  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
      return;
    }

    const payload = {
      salesman_id: null,
      start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
    };

    dispatch(getSalesmanRedemptionStats(payload, userInfo.access_token));
  }, [dispatch, userInfo, navigate, startDate, endDate]);

  // Salesman filter — used only in summary mode ("" = all salesmen)
  const [summarySalesmanFilter, setSummarySalesmanFilter] = useState("");

  // Reset the salesman filter whenever the card type changes
  useEffect(() => {
    setSummarySalesmanFilter("");
  }, [cardType]);

  const handleSalesmanChange = (e) => {
    navigate(`/dashboard/redeemedstatistcis/${e.target.value}`, {
      state: { dateRange: [startDate, endDate] },
    });
  };

  const getFlattenedQrCodes = (statusFilter = null) => {
    return salesmanData.flatMap((row) =>
      (row.qr_codes || [])
        .filter((qr) => !statusFilter || qr.status === statusFilter)
        .map((qr) => ({
          ...qr,
          salesman_user_id: row.salesman.user_id,
          salesman_name: row.salesman.name,
          salesman_mobile: row.salesman.mobile,
        }))
    );
  };

  const selectedSalesman =
    mode === "salesman"
      ? salesmanData.find((row) => row.salesman.user_id === salesmanId)
      : null;

  const qrRows =
    mode === "summary" && config?.type === "qr"
      ? getFlattenedQrCodes(config.statusFilter).filter(
          (qr) =>
            !summarySalesmanFilter ||
            qr.salesman_user_id === summarySalesmanFilter
        )
      : [];

  const summarySalesmenRows =
    mode === "summary" && config?.type === "salesmen"
      ? salesmanData.filter(
          (row) =>
            !summarySalesmanFilter ||
            row.salesman.user_id === summarySalesmanFilter
        )
      : [];

  const pageTitle =
    mode === "salesman"
      ? selectedSalesman
        ? `Redemption Detail — ${selectedSalesman.salesman.name}`
        : "Salesman Redemption Detail"
      : config?.title || "Redemption Detail";

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto px-2">
        <div className="flex items-center space-x-3 py-4">
          <button
            onClick={() => navigate("/dashboard/redeemedstatistcis")}
            className="hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold text-amber-600 mb-1">
            {pageTitle}
          </h2>
        </div>

        {/* Filters — salesman selector (salesman mode only) + date range (both modes) */}
        <div className="w-full flex flex-col sm:flex-row items-center justify-start gap-4 mb-6 bg-white p-4 rounded-lg shadow-md border-l-4 border-amber-500">
          {mode === "salesman" && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Salesman
              </label>
              <select
                value={salesmanId}
                onChange={handleSalesmanChange}
                className="px-4 py-2 border border-gray-950 rounded-md text-sm min-w-[220px]"
              >
                {salesmanData.map((row) => (
                  <option
                    key={row.salesman.user_id}
                    value={row.salesman.user_id}
                  >
                    {row.salesman.name} ({row.salesman.mobile})
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "summary" && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Salesman
              </label>
              <select
                value={summarySalesmanFilter}
                onChange={(e) => setSummarySalesmanFilter(e.target.value)}
                className="px-4 py-2 border border-gray-950 rounded-md text-sm min-w-[220px]"
              >
                <option value="">All Salesmen</option>
                {salesmanData.map((row) => (
                  <option
                    key={row.salesman.user_id}
                    value={row.salesman.user_id}
                  >
                    {row.salesman.name} ({row.salesman.mobile})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Date Range
            </label>
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              isClearable
              placeholderText="Select date range"
              dateFormat="dd MMM yyyy"
              className="px-4 py-2 border border-gray-950 rounded-md text-sm placeholder-gray-950"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-8">Loading ...</p>
        ) : error ? (
          <p className="text-center text-red-500 py-8">{error}</p>
        ) : mode === "salesman" ? (
          !selectedSalesman ? (
            <p className="text-center text-gray-500 py-8">
              No data found for this salesman in the selected date range.
            </p>
          ) : (
            <>
              <div className="mb-6 flex flex-wrap gap-6 bg-white p-4 rounded-xl shadow-md">
                <p>
                  <strong>Salesman:</strong>{" "}
                  {selectedSalesman.salesman.name}
                </p>
                <p>
                  <strong>Mobile:</strong> {selectedSalesman.salesman.mobile}
                </p>
                <p>
                  <strong>Total Accumulated:</strong>{" "}
                  {selectedSalesman.summary.total_accumulated}
                </p>
                <p>
                  <strong>Total Redeemed:</strong>{" "}
                  <span className="text-pink-600 font-semibold">
                    {selectedSalesman.summary.total_redeemed}
                  </span>
                </p>
                <p>
                  <strong>Total Scanned:</strong>{" "}
                  {selectedSalesman.summary.total_scanned}
                </p>
              </div>

              {selectedSalesman.qr_codes.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No QR codes scanned in this date range.
                </p>
              ) : (
                <div className="overflow-x-auto rounded">
                  <table className="border border-amber-500 rounded-lg min-w-full bg-white text-sm text-left text-gray-950">
                    <thead className="bg-amber-500 text-xs uppercase text-white">
                      <tr>
                        <th className="py-3 px-4 border-b">QR ID</th>
                        <th className="py-3 px-4 border-b">Status</th>
                        <th className="py-3 px-4 border-b">Scanned Date</th>
                        <th className="py-3 px-4 border-b">Customer</th>
                        <th className="py-3 px-4 border-b">
                          Customer Mobile
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSalesman.qr_codes.map((qr) => (
                        <tr key={qr.qr_id} className="border-t">
                          <td className="px-2 py-4 border-b-gray-400">
                            {qr.qr_id}
                          </td>
                          <td className="px-2 py-4 border-b-gray-400">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                qr.status === "Redeemed"
                                  ? "bg-pink-100 text-pink-700"
                                  : "bg-purple-100 text-purple-700"
                              }`}
                            >
                              {qr.status}
                            </span>
                          </td>
                          <td className="px-2 py-4 border-b-gray-400">
                            {qr.scanned_date}
                          </td>
                          <td className="px-2 py-4 border-b-gray-400">
                            {qr.customer}
                          </td>
                          <td className="px-2 py-4 border-b-gray-400">
                            {qr.customer_mobile}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )
        ) : !config ? (
          <p className="text-center text-red-500 py-8">Unknown view.</p>
        ) : config.type === "salesmen" ? (
          summarySalesmenRows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No salesmen found for the selected filters.
            </p>
          ) : (
            <div className="overflow-x-auto rounded">
              <table className="border border-amber-500 rounded-lg min-w-full bg-white text-sm text-left text-gray-950">
                <thead className="bg-amber-500 text-xs uppercase text-white">
                  <tr>
                    <th className="py-3 px-4 border-b">Salesman</th>
                    <th className="py-3 px-4 border-b">Mobile</th>
                    <th className="py-3 px-4 border-b">Accumulated</th>
                    <th className="py-3 px-4 border-b">Redeemed</th>
                    <th className="py-3 px-4 border-b">Total Scanned</th>
                    <th className="py-3 px-4 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {summarySalesmenRows.map((row) => (
                    <tr
                      key={row.salesman.user_id}
                      className="border-t hover:bg-amber-50"
                    >
                      <td className="px-2 py-4 border-b-gray-400">
                        {row.salesman.name}
                      </td>
                      <td className="px-2 py-4 border-b-gray-400">
                        {row.salesman.mobile}
                      </td>
                      <td className="px-2 py-4 border-b-gray-400">
                        {row.summary.total_accumulated}
                      </td>
                      <td className="px-2 py-4 border-b-gray-400 font-semibold text-pink-600">
                        {row.summary.total_redeemed}
                      </td>
                      <td className="px-2 py-4 border-b-gray-400">
                        {row.summary.total_scanned}
                      </td>
                      <td className="px-2 py-4 border-b-gray-400">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/redeemedstatistcis/${row.salesman.user_id}`,
                              { state: { dateRange: [startDate, endDate] } }
                            )
                          }
                          className="text-amber-600 hover:text-amber-700 font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : qrRows.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No QR codes found for the selected filters.
          </p>
        ) : (
          <div className="overflow-x-auto rounded">
            <table className="border border-amber-500 rounded-lg min-w-full bg-white text-sm text-left text-gray-950">
              <thead className="bg-amber-500 text-xs uppercase text-white">
                <tr>
                  <th className="py-3 px-4 border-b">QR ID</th>
                  <th className="py-3 px-4 border-b">Status</th>
                  <th className="py-3 px-4 border-b">Salesman</th>
                  <th className="py-3 px-4 border-b">Scanned Date</th>
                  <th className="py-3 px-4 border-b">Customer</th>
                  <th className="py-3 px-4 border-b">Customer Mobile</th>
                </tr>
              </thead>
              <tbody>
                {qrRows.map((qr, idx) => (
                  <tr key={`${qr.qr_id}-${idx}`} className="border-t">
                    <td className="px-2 py-4 border-b-gray-400">
                      {qr.qr_id}
                    </td>
                    <td className="px-2 py-4 border-b-gray-400">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          qr.status === "Redeemed"
                            ? "bg-pink-100 text-pink-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {qr.status}
                      </span>
                    </td>
                    <td className="px-2 py-4 border-b-gray-400">
                      {qr.salesman_name}
                    </td>
                    <td className="px-2 py-4 border-b-gray-400">
                      {qr.scanned_date}
                    </td>
                    <td className="px-2 py-4 border-b-gray-400">
                      {qr.customer}
                    </td>
                    <td className="px-2 py-4 border-b-gray-400">
                      {qr.customer_mobile}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SalesmanRedemptionDetail;